import React, { useState, useEffect, useCallback, useRef } from "react";
import { Layout, Button, notification, DatePicker, Space, message, Table, Tag, Card, Select } from "antd";
import jsPDF from 'jspdf';
import { Row, Form, Col } from "antd";
import { DownloadOutlined, FilterOutlined, ClearOutlined, SearchOutlined } from "@ant-design/icons";
import * as XLSX from 'xlsx';
import autoTable from 'jspdf-autotable';
import utc from "dayjs/plugin/utc";

import TopHeader from "../common/TopHeader/TopHeader";
import Sidebar from "../common/Sidebar/Sidebar";
// import FooterDashboard from "../common/FooterDashboard/FooterDashboard";
import dayjs from "dayjs";
import ReportsApi from "../common/Apis/ReportsApi";
import Pagination from "../common/Pagination/Pagination"; // Import your custom Pagination
import "../common/Pagination/Pagination.css"; // Import the CSS

const AftercookReport = () => {
    const { Content } = Layout;
    const [formAdd] = Form.useForm();
    const [afterCookData, setAfterCookData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);
    
    // Filter states
    const [batchCodeFilter, setBatchCodeFilter] = useState(null);
    const [cookedByFilter, setCookedByFilter] = useState(null);
    const [appliedBatchCodeFilter, setAppliedBatchCodeFilter] = useState(null);
    const [appliedCookedByFilter, setAppliedCookedByFilter] = useState(null);
    
    // Filter options
    const [batchOptions, setBatchOptions] = useState([]);
    const [cookedByOptions, setCookedByOptions] = useState([]);
    
    // Pagination state using your custom pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const hasActiveFilters = !!(batchCodeFilter || cookedByFilter);

    const displayData = hasActiveFilters
        ? filteredData
        : afterCookData;

    const PageLoading = useCallback(() => {
        setIsLoading(true);
        setTimeout(() => {
            setIsLoading(false);
        }, 800);
    }, []);

    // Extract unique batch codes from data
    const extractBatchOptions = (data) => {
        const batches = new Set();
        data.forEach((item) => {
            if (item?.batchCodeOfTheCookedProduct) {
                batches.add(item.batchCodeOfTheCookedProduct);
            }
        });
        setBatchOptions(Array.from(batches).sort());
    };

    // Extract unique cooked by names from data
    const extractCookedByOptions = (data) => {
        const cookedBy = new Set();
        data.forEach((item) => {
            if (item?.createdBy) {
                cookedBy.add(item.createdBy);
            }
        });
        setCookedByOptions(Array.from(cookedBy).sort());
    };

    // Apply filters
    const applyFilters = useCallback((data) => {
        if (!data || data.length === 0) {
            setFilteredData([]);
            setTotalItems(0);
            setTotalPages(0);
            return;
        }

        let filtered = [...data];

       if (batchCodeFilter) {
    filtered = filtered.filter(item =>
        item?.batchCodeOfTheCookedProduct &&
        item.batchCodeOfTheCookedProduct.toLowerCase() === batchCodeFilter.toLowerCase()
    );
}

if (cookedByFilter) {
    filtered = filtered.filter(item =>
        item?.createdBy &&
        item.createdBy.toLowerCase() === cookedByFilter.toLowerCase()
    );
}

        setFilteredData(filtered);
        setTotalItems(filtered.length);
        setTotalPages(Math.ceil(filtered.length / pageSize));
        setCurrentPage(1);
   }, [batchCodeFilter, cookedByFilter, pageSize]);

    // Update filtered data when afterCookData or applied filters change
    useEffect(() => {
        if (afterCookData.length > 0) {
            extractBatchOptions(afterCookData);
            extractCookedByOptions(afterCookData);
            applyFilters(afterCookData);
        } else {
            setFilteredData([]);
            setTotalItems(0);
            setTotalPages(0);
        }
    }, [afterCookData, batchCodeFilter, cookedByFilter]);


    // Reset filters
    const resetFilters = () => {
        setBatchCodeFilter(null);
        setCookedByFilter(null);
        setFilteredData(afterCookData);
        setTotalItems(afterCookData.length);
        setTotalPages(Math.ceil(afterCookData.length / pageSize));
        setCurrentPage(1);
        message.info("All filters have been reset");
    };

    const handleExportToPdf = () => {
        const dataToExport = filteredData.length > 0 ? filteredData : afterCookData;
        
        if (!dataToExport || dataToExport.length === 0) {
            message.warning("No data available to export");
            return;
        }

        setExportLoading(true);

        try {
            const pdfData = [];

            dataToExport.forEach((item) => {
                let allergensString = '';

                if (item?.productionDTO?.allergens) {
                    const allergenList = Array.isArray(item.productionDTO.allergens)
                        ? item.productionDTO.allergens
                        : item.productionDTO.allergens.split(',');

                    allergensString = allergenList
                        .map(a => a.trim())
                        .join(', ');
                }

                let correctiveAction = item?.description || '-';

                if (correctiveAction !== '-') {
                    if (correctiveAction.toLowerCase().includes('rework')) {
                        correctiveAction = 'Rework';
                    } else if (correctiveAction.toLowerCase().includes('reject')) {
                        correctiveAction = 'Rejected';
                    } else if (correctiveAction.toLowerCase().includes('approved')) {
                        correctiveAction = 'Approved';
                    }
                }

                pdfData.push([
                    formatDate(item.dateOfCooking),
                    convertTo12HourFormat(item.timeOfCookingFinished),
                    item?.productionName || '-',
                    item?.temperatureOfTheProductAfterCook &&
                    item.temperatureOfTheProductAfterCook !== 'N/A'
                        ? `${item.temperatureOfTheProductAfterCook}°C`
                        : (item?.temperatureOfTheProductAfterCook || '-'),
                    item?.batchCodeOfTheCookedProduct || '-',
                    formatDate(item.oubd),
                    formatDate(item.newSellByDateOfTheCookedProduct),
                    `${(() => {
                        const value = Number(item?.quantityOfTheCookedProduct || 0);

                        const normalStr = value.toLocaleString("fullwide", {
                            useGrouping: false,
                            maximumFractionDigits: 20,
                        });

                        const match = normalStr.match(/^-?\d+(?:\.\d{0,2})?/);

                        return match ? match[0] : "0";
                    })()} ${item?.unit || ''}`,
                    item?.createdBy || '-',
                    allergensString || '-',
                    correctiveAction
                ]);
            });

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });

            doc.setFontSize(16);
            doc.text('After Cook Report', 14, 15);

            const startDate = formAdd.getFieldValue('startDate');
            const endDate = formAdd.getFieldValue('endDate');

            let filterInfo = '';
            if (startDate && endDate) {
                filterInfo += `Date Range: ${dayjs(startDate).format('DD/MM/YYYY')} - ${dayjs(endDate).format('DD/MM/YYYY')}`;
            }
           if (batchCodeFilter) {
             filterInfo += ` | Batch: ${batchCodeFilter}`;
                }
                if (cookedByFilter) {
                    filterInfo += ` | Cooked By: ${cookedByFilter}`;
                }
            filterInfo += ` | Total Records: ${dataToExport.length}`;

            doc.setFontSize(10);
            doc.text(filterInfo, 14, 22);

            autoTable(doc, {
                startY: 28,
                head: [[
                    'Date',
                    'Time',
                    'Product Description',
                    'Temp (°C)',
                    'Batch Code',
                    'OUBD',
                    'Sell By Date',
                    'Qty',
                    'Cooked By',
                    'Allergens',
                    'Corrective Action'
                ]],
                body: pdfData,
                styles: {
                    fontSize: 7,
                    cellPadding: 2
                },
                headStyles: {
                    fillColor: [79, 129, 189],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold'
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245]
                },
                margin: {
                    left: 5,
                    right: 5
                }
            });

            let fileName = `After_Cook_Report_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.pdf`;

            if (startDate && endDate) {
                const startStr = dayjs(startDate).format('YYYY-MM-DD');
                const endStr = dayjs(endDate).format('YYYY-MM-DD');
                const batchStr = batchCodeFilter ? `_${batchCodeFilter}` : '';
                const cookedStr = cookedByFilter ? `_${cookedByFilter}` : '';
                fileName = `After_Cook_Report_${startStr}_to_${endStr}${batchStr}${cookedStr}.pdf`;
            }

            doc.save(fileName);

            message.success("After cook report exported successfully!");

        } catch (error) {
            console.error("Error exporting after cook report:", error);
            message.error("Failed to export after cook report");
        } finally {
            setExportLoading(false);
        }
    };

    const handlePrintButtonClick2 = () => {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4'
        });

        const fontSize = 12;
        const margin = 5;
        const cellWidthFactor = 0.295;
        const cellHeightFactor = 0.2;

        doc.setFontSize(fontSize);

        const table = document.querySelector('.table');
        const rows = table.querySelectorAll('tr');

        const pageTitle = 'After Cook Reports';
        const pageTitleWidth = doc.getStringUnitWidth(pageTitle) * fontSize / doc.internal.scaleFactor;
        const pageTitleXPos = (doc.internal.pageSize.width - pageTitleWidth) / 2;
        doc.text(pageTitleXPos, margin, pageTitle);

        doc.setFontSize(fontSize);
        const currentDate = new Date().toLocaleString();
        const dateTimeWidth = doc.getStringUnitWidth(currentDate) * fontSize / doc.internal.scaleFactor;
        const dateTimeXPos = doc.internal.pageSize.width - margin - dateTimeWidth;
        doc.text(dateTimeXPos, margin, currentDate);

        let yPos = margin + fontSize;

        rows.forEach((row, rowIndex) => {
            const cells = row.querySelectorAll('th, td');
            let xPos = margin;

            cells.forEach((cell, cellIndex) => {
                const cellText = cell.textContent.trim();
                const cellWidth = cell.offsetWidth * cellWidthFactor;
                const cellHeight = row.offsetHeight * cellHeightFactor;

                if (yPos + cellHeight > doc.internal.pageSize.height - margin) {
                    doc.addPage();
                    yPos = margin + fontSize;
                    const pageTitle = 'After Cook Reports';
                    const pageTitleWidth = doc.getStringUnitWidth(pageTitle) * fontSize / doc.internal.scaleFactor;
                    const pageTitleXPos = (doc.internal.pageSize.width - pageTitleWidth) / 2;
                    doc.text(pageTitleXPos, margin, pageTitle);

                    doc.setFontSize(fontSize);
                    const currentDate = new Date().toLocaleString();
                    const dateTimeWidth = doc.getStringUnitWidth(currentDate) * fontSize / doc.internal.scaleFactor;
                    const dateTimeXPos = doc.internal.pageSize.width - margin - dateTimeWidth;
                    doc.text(dateTimeXPos, margin, currentDate);
                }

                const isHeader = cell.tagName.toLowerCase() === 'th';
                const fontWeight = isHeader ? 'bold' : 'normal';

                doc.setFont(undefined, fontWeight);

                const textWidth = doc.getStringUnitWidth(cellText) * fontSize / doc.internal.scaleFactor;
                const cellPadding = 2;
                let xPosAlign = xPos + (cellWidth - textWidth - cellPadding) / 2;

                if (!isHeader) {
                    xPosAlign = xPos;
                }
                doc.rect(xPos, yPos, cellWidth, cellHeight);
                doc.text(xPosAlign + 1, yPos + 5, cellText);

                xPos += cellWidth;
            });

            yPos += row.offsetHeight * cellHeightFactor;
        });

        window.open(doc.output('bloburl'), '_blank');
    };

    const fetchAfterCookData = useCallback(async (page = 1, pageSize = 10) => {
        const startDate = formAdd.getFieldValue('startDate');
        const endDate = formAdd.getFieldValue('endDate');
        
        if (!startDate || !endDate) {
            return;
        }
        
        try {
            PageLoading();

            const startTimestamp = dayjs
                .utc(startDate.format('YYYY-MM-DD'))
                .startOf('day')
                .valueOf();

            const endTimestamp = dayjs
                .utc(endDate.format('YYYY-MM-DD'))
                .add(1, 'day')
                .startOf('day')
                .valueOf();

            const response = await ReportsApi.AfterCookReport(startTimestamp, endTimestamp);
            
            let dataArray = [];
            let totalElements = 0;
            
            if (response?.payload) {
                dataArray = response.payload;
                totalElements = response.payload.length;
            } else if (response?.payloadDto?.content) {
                dataArray = response.payloadDto.content;
                totalElements = response.payloadDto.totalElements;
            } else if (Array.isArray(response)) {
                dataArray = response;
                totalElements = response.length;
            } else if (response?.content) {
                dataArray = response.content;
                totalElements = response.totalElements;
            }
            
            setAfterCookData(dataArray);
            // Reset filters when new data is loaded
            setBatchCodeFilter(null);
            setCookedByFilter(null);
            setTotalItems(totalElements);
            setTotalPages(Math.ceil(totalElements / pageSize));
            setCurrentPage(page);
            setPageSize(pageSize);
        } catch (err) {
            console.log(err);
            if (err.apiError?.status) {
                notification.open({
                    message: (
                        <span style={{ fontWeight: "bold" }}>{err.apiError.status}</span>
                    ),
                    description: err.apiError.message,
                    closeIcon: false,
                    duration: 3,
                    style: {
                        backgroundColor: "#fff2f0",
                        color: "red",
                    },
                });
            } else {
                notification.error({
                    message: "Error",
                    description: err?.message || "Failed to fetch after cook report",
                });
            }
        }
    }, [PageLoading]);

    const handleFilter = (data) => {
        setFilterLoading(true);
        fetchAfterCookData(1, pageSize);
        setTimeout(() => {
            setFilterLoading(false);
        }, 800);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    function formatDate(timestamp) {
        if (!timestamp) return '-';
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) return '-';
        const options = { day: "2-digit", month: "short", year: "numeric" };
        return date.toLocaleDateString("en-US", options);
    }

    function convertTo12HourFormat(timeString) {
        if (!timeString) return '';

        const [hours, minutes] = timeString.split(':');
        let formattedTime = '';

        let formattedHours = parseInt(hours, 10);
        const ampm = formattedHours >= 12 ? 'PM' : 'AM';
        formattedHours = formattedHours % 12 || 12;

        const formattedMinutes = minutes.padStart(2, '0');

        formattedTime = `${formattedHours}:${formattedMinutes} ${ampm}`;
        return formattedTime;
    }

    function convertToUnixTimestamp(dateString) {
        const parts = dateString.split("/");
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        const unixTimestamp = Date.UTC(year, month, day);
        return unixTimestamp;
    }

    const handleClear = () => {
        setIsLoading(true);
        setAfterCookData([]);
        setFilteredData([]);
        formAdd.resetFields();
        // Reset filters
        setBatchCodeFilter(null);
        setCookedByFilter(null);
        setAppliedBatchCodeFilter(null);
        setAppliedCookedByFilter(null);
        const tomorrow = dayjs().add(1, 'day');
        const today = dayjs();
        formAdd.setFieldsValue({
            startDate: today,
            endDate: tomorrow,
        });
        setTimeout(() => {
            setIsLoading(false);
            fetchAfterCookData(1, 10);
        }, 800);
    };

    const getAllergensTag = (allergens) => {
        if (!allergens) return '-';
        const allergenList = Array.isArray(allergens) ? allergens : allergens.split(',');
        return (
            <Space size={[0, 4]} wrap>
                {allergenList.map((allergen, index) => (
                    <Tag key={index} color="orange">{allergen.trim()}</Tag>
                ))}
            </Space>
        );
    };

    const getCorrectiveActionTag = (description) => {
        if (!description) return '-';
        if (description.toLowerCase().includes('rework')) {
            return <Tag color="orange">Rework</Tag>;
        } else if (description.toLowerCase().includes('reject')) {
            return <Tag color="red">Rejected</Tag>;
        } else if (description.toLowerCase().includes('approved')) {
            return <Tag color="green">Approved</Tag>;
        }
        return <Tag color="blue">{description}</Tag>;
    };

    // Get current page data for client-side pagination
    const getCurrentPageData = () => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return displayData.slice(startIndex, endIndex);
    };

    const currentPageData = getCurrentPageData();

    useEffect(() => {
        const tomorrow = dayjs().add(1, 'day');
        const today = dayjs();
        
        formAdd.setFieldsValue({
            startDate: today,
            endDate: tomorrow,
        });
        
        fetchAfterCookData(1, 10);
    }, []);

    const columns = [
        {
            title: 'Date',
            dataIndex: 'dateOfCooking',
            key: 'dateOfCooking',
            width: '10%',
            render: (date) => formatDate(date),
        },
        {
            title: 'Time',
            dataIndex: 'timeOfCookingFinished',
            key: 'timeOfCookingFinished',
            width: '10%',
            render: (time) => convertTo12HourFormat(time),
        },
        {
            title: 'Product Description',
            dataIndex: 'productionName',
            key: 'productionName',
            width: '12%',
            render: (text) => text || '-',
        },
        {
            title: 'Temp',
            dataIndex: 'temperatureOfTheProductAfterCook',
            key: 'temperatureOfTheProductAfterCook',
            width: '8%',
            render: (temp) => temp && temp !== 'N/A' ? `${temp}°C` : (temp || '-'),
        },
        {
            title: 'Batch Code',
            dataIndex: 'batchCodeOfTheCookedProduct',
            key: 'batchCodeOfTheCookedProduct',
            width: '12%',
            render: (code) => {
                if (!code) return '-';
                return (
                    <div style={{
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        whiteSpace: 'nowrap',
                        maxWidth: '200px',
                        padding: '0px 0px 20px',
                        display: 'block',
                    }}>
                        {code}
                    </div>
                );
            },
        },
        {
            title: 'OUBD (Original Used by Date)',
            dataIndex: 'oubd',
            key: 'oubd',
            width: '12%',
            render: (date) => formatDate(date),
        },
        {
            title: 'Sell By Date',
            dataIndex: 'newSellByDateOfTheCookedProduct',
            key: 'newSellByDateOfTheCookedProduct',
            width: '10%',
            render: (date) => formatDate(date),
        },
        {
            title: 'Qty',
            dataIndex: 'quantityOfTheCookedProduct',
            key: 'quantityOfTheCookedProduct',
            width: '8%',
            align: 'right',
            render: (qty, record) => {
                const value = Number(qty || 0);

                const normalStr = value.toLocaleString("fullwide", {
                    useGrouping: false,
                    maximumFractionDigits: 20,
                });

                const match = normalStr.match(/^-?\d+(?:\.\d{0,2})?/);

                return `${match ? match[0] : "0"} ${record?.unit || ''}`;
            },
        },
        {
            title: 'Cooked by',
            dataIndex: 'createdBy',
            key: 'createdBy',
            width: '10%',
            render: (name) => name || '-',
        },
        {
            title: 'Allergens',
            dataIndex: ['productionDTO', 'allergens'],
            key: 'allergens',
            width: '10%',
            render: (allergens) => getAllergensTag(allergens),
        },
        {
            title: 'Corrective Action',
            dataIndex: 'description',
            key: 'description',
            width: '10%',
            render: (description) => getCorrectiveActionTag(description),
        },
    ];

    const centeredColumns = columns.map((column) => ({
        ...column,
        align: 'center'
    }));

    return (
        <Layout style={{ minHeight: "100vh" }}>
            <Sidebar />
            <Layout>
                <TopHeader onPrintResponse2={handlePrintButtonClick2} />
                {isLoading && (
                    <div className="spinner-overlay" id="spinnerOverlay">
                        <div className="spinner"></div>
                    </div>
                )}

                <Content
                    className="form-content nav-content"
                    style={{
                        margin: "14px 16px 0",
                        padding: "30px",
                        background: "white",
                    }}
                >
                    <div>
                        {/* Filter Section - Separated Card */}
                        <Card 
                            style={{ 
                                marginBottom: '24px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '10px' }}>
                                <Space>
                                    <FilterOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>Date Range Filter</span>
                                </Space>
                            </div>
                            
                            <Form
                                form={formAdd}
                                name="aftercook_filter_form"
                                layout="vertical"
                                onFinish={handleFilter}
                            >
                                <Row gutter={[16, 0]}>
                                    <Col lg={8} md={12} sm={24} xs={24}>
                                        <Form.Item
                                            name="startDate"
                                            label="Start Date"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please Select Start Date.",
                                                },
                                            ]}
                                        >
                                            <DatePicker
                                                format={"DD/MM/YYYY"}
                                                placeholder="Select start date"
                                                style={{ width: '100%', height: '40px' }}
                                                allowClear={false}
                                                suffixIcon={null}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col lg={8} md={12} sm={24} xs={24}>
                                        <Form.Item
                                            name="endDate"
                                            label="End Date"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please Select End Date.",
                                                },
                                            ]}
                                        >
                                            <DatePicker
                                                format={"DD/MM/YYYY"}
                                                placeholder="Select end date"
                                                style={{ width: '100%', height: '40px' }}
                                                allowClear={false}
                                                suffixIcon={null}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col lg={8} md={24} sm={24} xs={24}>
                                        <Form.Item label="Actions">
                                            <Space size="middle" wrap>
                                                <Button
                                                    type="primary"
                                                    htmlType="submit"
                                                    loading={filterLoading}
                                                    icon={<FilterOutlined />}
                                                    style={{ height: '40px', minWidth: '100px' }}
                                                >
                                                    Apply Date Filter
                                                </Button>
                                                <Button
                                                    onClick={() => handleClear()}
                                                    icon={<ClearOutlined />}
                                                    style={{ height: '40px', minWidth: '100px' }}
                                                >
                                                    Clear All
                                                </Button>
                                            </Space>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form>
                        </Card>

                        {/* Advanced Filters Section */}
                        <Card 
                            style={{ 
                                marginBottom: '24px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                background: '#fafafa'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '10px' }}>
                                <Space>
                                    <SearchOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>Advanced Filters</span>
                                    <span style={{ fontSize: '12px', color: '#999' }}>
                                        (Filter by Batch Code and Cooked By)
                                    </span>
                                </Space>
                            </div>
                            
                            <Row gutter={[16, 16]}>
                                <Col lg={8} md={12} sm={12} xs={24}>
                                    <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 500, color: '#555' }}>
                                        Batch Code
                                    </div>
                                    <Select
                                        placeholder="All Batches"
                                        style={{ width: '100%' }}
                                        value={batchCodeFilter}
                                        onChange={(value) => {
                                            setBatchCodeFilter(value);
                                        }}
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) =>
                                            option?.children?.toLowerCase().includes(input.toLowerCase())
                                        }
                                    >
                                        {batchOptions.map(batch => (
                                            <Select.Option key={batch} value={batch}>{batch}</Select.Option>
                                        ))}
                                    </Select>
                                </Col>

                                <Col lg={8} md={12} sm={12} xs={24}>
                                    <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 500, color: '#555' }}>
                                        Cooked By
                                    </div>
                                    <Select
                                        placeholder="All Cooked By"
                                        style={{ width: '100%' }}
                                        value={cookedByFilter}
                                        onChange={(value) => {
                                            setCookedByFilter(value);
                                        }}
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) =>
                                            option?.children?.toLowerCase().includes(input.toLowerCase())
                                        }
                                    >
                                        {cookedByOptions.map(name => (
                                            <Select.Option key={name} value={name}>{name}</Select.Option>
                                        ))}
                                    </Select>
                                </Col>

                                <Col lg={8} md={12} sm={24} xs={24} style={{ display: 'flex', alignItems: 'flex-end' }}>
                                    <Space>
                                    
                                        <Button
                                            onClick={resetFilters}
                                            icon={<ClearOutlined />}
                                            style={{ height: '36px' }}
                                            disabled={!hasActiveFilters}
                                        >
                                            Reset Filters
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                            
                            <div style={{ marginTop: '12px', padding: '8px 12px', background: '#e6f7ff', borderRadius: '4px', fontSize: '12px', color: '#1890ff' }}>
                                <strong>Total Records:</strong> {afterCookData.length} | 
                                <strong> Displaying:</strong> {displayData.length} records
                                {hasActiveFilters && (
                                    <span style={{ marginLeft: '8px' }}>
                                        (Filtered by: 
                                        {batchCodeFilter && ` Batch: ${batchCodeFilter}`}
                                        {cookedByFilter && `, Cooked By: ${cookedByFilter}`}
                                        )
                                    </span>
                                )}
                            </div>
                        </Card>

                        {/* Report Results Section */}
                        <Card 
                            style={{ 
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ 
                                marginBottom: '16px', 
                                borderBottom: '1px solid #f0f0f0', 
                                paddingBottom: '10px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '10px'
                            }}>
                                <Space>
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>After Cook Report Results</span>
                                    <span style={{ fontSize: '12px', color: '#999' }}>
                                        {displayData.length > 0 && `(${displayData.length} records found)`}
                                        {displayData.length !== afterCookData.length && afterCookData.length > 0 && 
                                            ` (filtered from ${afterCookData.length} total)`
                                        }
                                    </span>
                                </Space>
                                <Button
                                    type="primary"
                                    onClick={handleExportToPdf}
                                    loading={exportLoading}
                                    icon={<DownloadOutlined />}
                                    style={{ 
                                        height: '36px',
                                        backgroundColor: '#52c41a',
                                        borderColor: '#52c41a'
                                    }}
                                >
                                    Export Aftercook Report PDF
                                </Button>
                            </div>
                            
                            <div className="dateFilterTable" style={{ overflowX: 'auto' }}>
                                <Table
                                    columns={centeredColumns}
                                    dataSource={currentPageData}
                                    rowKey="id"
                                    pagination={false}
                                    bordered
                                    className="users-table Reportable"
                                    scroll={{ x: 'max-content' }}
                                />
                            </div>
                            
                            {/* Custom Pagination */}
                            {displayData.length > 0 && (
                                <div style={{ marginTop: '20px' }}>
                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                        maxVisiblePages={5}
                                        showPageInfo={true}
                                        totalItems={totalItems}
                                        pageSize={pageSize}
                                    />
                                </div>
                            )}
                        </Card>
                    </div>
                </Content>

                {/* <FooterDashboard /> */}
            </Layout>
        </Layout>
    );
};

export default AftercookReport;